/**
 * Vehicle search params contract — enums, defaults, sort/city maps (Milestone 4).
 */

import { VEHICLE_SEARCH_SORT } from "./vehicleSearchSortConstants";

export { VEHICLE_SEARCH_SORT };

export const VEHICLE_SEARCH_SORT_OPTIONS = [
  { value: VEHICLE_SEARCH_SORT.RECOMMENDED, label: "Mejor coincidencia" },
  { value: VEHICLE_SEARCH_SORT.NEWEST, label: "Más recientes" },
  { value: VEHICLE_SEARCH_SORT.LOWEST_PRICE, label: "Precio menor" },
  { value: VEHICLE_SEARCH_SORT.HIGHEST_PRICE, label: "Precio mayor" },
  { value: VEHICLE_SEARCH_SORT.LOWEST_MILEAGE, label: "Menor kilometraje" },
  { value: VEHICLE_SEARCH_SORT.NEWEST_YEAR, label: "Año más reciente" },
  { value: VEHICLE_SEARCH_SORT.BEST_VALUE, label: "Mejor valor" },
  { value: VEHICLE_SEARCH_SORT.VERIFIED_DEALERS_FIRST, label: "Verificados primero" },
  { value: VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED, label: "Confirmados recientemente" },
];

export const SORT_TO_API_MAP = {
  // Client-ranked sorts fetch a broad newest page, then re-rank locally.
  [VEHICLE_SEARCH_SORT.RECOMMENDED]: "newest",
  [VEHICLE_SEARCH_SORT.NEWEST]: "newest",
  [VEHICLE_SEARCH_SORT.LOWEST_PRICE]: "price_low_to_high",
  [VEHICLE_SEARCH_SORT.HIGHEST_PRICE]: "price_high_to_low",
  [VEHICLE_SEARCH_SORT.LOWEST_MILEAGE]: "mileage_low_to_high",
  [VEHICLE_SEARCH_SORT.NEWEST_YEAR]: "year_new_to_old",
  [VEHICLE_SEARCH_SORT.BEST_VALUE]: "newest",
  [VEHICLE_SEARCH_SORT.VERIFIED_DEALERS_FIRST]: "newest",
  [VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED]: "newest",
};

export const LEGACY_SORT_ALIASES = {
  match: VEHICLE_SEARCH_SORT.RECOMMENDED,
  priceAsc: VEHICLE_SEARCH_SORT.LOWEST_PRICE,
  priceDesc: VEHICLE_SEARCH_SORT.HIGHEST_PRICE,
  yearDesc: VEHICLE_SEARCH_SORT.NEWEST_YEAR,
  kmAsc: VEHICLE_SEARCH_SORT.LOWEST_MILEAGE,
  best_value_score: VEHICLE_SEARCH_SORT.BEST_VALUE,
  price_low_to_high: VEHICLE_SEARCH_SORT.LOWEST_PRICE,
  price_high_to_low: VEHICLE_SEARCH_SORT.HIGHEST_PRICE,
  year_new_to_old: VEHICLE_SEARCH_SORT.NEWEST_YEAR,
  mileage_low_to_high: VEHICLE_SEARCH_SORT.LOWEST_MILEAGE,
};

export const VEHICLE_SEARCH_CITY_OPTIONS = [
  "Medellín",
  "Envigado",
  "Sabaneta",
  "Itagüí",
  "Bello",
  "Rionegro",
  "Bogotá",
  "Cali",
];

export const CITY_SLUG_TO_LABEL = {
  medellin: "Medellín",
  envigado: "Envigado",
  sabaneta: "Sabaneta",
  itagui: "Itagüí",
  bello: "Bello",
  rionegro: "Rionegro",
  bogota: "Bogotá",
  cali: "Cali",
};

export const BODY_TYPE_UI_TO_CANONICAL = {
  todos: null,
  all: null,
  suv: "suv",
  SUV: "suv",
  camioneta: "suv",
  camionetas: "suv",
  "camioneta-suv": "suv",
  campero: "suv",
  camperos: "suv",
  sedán: "sedan",
  sedan: "sedan",
  Sedán: "sedan",
  automovil: "sedan",
  automóvil: "sedan",
  automoviles: "sedan",
  automóviles: "sedan",
  "automovil-usado": "sedan",
  hatchback: "hatchback",
  Hatchback: "hatchback",
  compact: "hatchback",
  pickup: "pickup",
  pickups: "pickup",
  Pickup: "pickup",
  van: "van",
  coupe: "coupe",
  wagon: "wagon",
  other: "other",
};

export const TRANSMISSION_UI_TO_CANONICAL = {
  todas: null,
  all: null,
  automática: "automatic",
  automatica: "automatic",
  automatico: "automatic",
  automatic: "automatic",
  mecánica: "manual",
  mecanica: "manual",
  manual: "manual",
  cvt: "cvt",
};

export const RECENTLY_CONFIRMED_DAYS = 7;

export const FILTER_CHIP_COPY = {
  heading: "Filtros activos",
  clearAll: "Limpiar filtros",
  removeAria: "Quitar filtro",
};

export const MOBILE_FILTER_COPY = {
  title: "Filtros",
  apply: "Aplicar filtros",
  clear: "Limpiar",
  viewResults: "Ver resultados",
};

export function createDefaultVehicleSearchParams() {
  return {
    keyword: null,
    city: null,
    department: null,
    make: null,
    model: null,
    yearMin: null,
    yearMax: null,
    priceMinCop: null,
    priceMaxCop: null,
    mileageMinKm: null,
    mileageMaxKm: null,
    transmission: null,
    fuelType: null,
    fuelTypes: [],
    bodyType: null,
    dealerVerified: false,
    fairPriceLabels: [],
    riskLevels: [],
    hideHighRisk: false,
    recentlyConfirmedOnly: false,
    hasPhotos: false,
    requirePhotos: false,
    metroArea: null,
    availabilityStatus: null,
  completeInfoOnly: false,
  sort: VEHICLE_SEARCH_SORT.RECOMMENDED,
  page: 1,
  pageSize: 24,
  view: null,
};
}

export function normalizeCanonicalSort(value) {
  if (!value) return VEHICLE_SEARCH_SORT.RECOMMENDED;
  const v = String(value).trim();
  if (Object.values(VEHICLE_SEARCH_SORT).includes(v)) return v;
  return LEGACY_SORT_ALIASES[v] || VEHICLE_SEARCH_SORT.RECOMMENDED;
}

export function mapSortToApiSort(sort) {
  const canonical = normalizeCanonicalSort(sort);
  return SORT_TO_API_MAP[canonical] || "newest";
}

export function resolveCityLabel(value) {
  if (!value || value === "all" || value === "Toda Colombia") return null;
  const slug = String(value).trim().toLowerCase();
  if (CITY_SLUG_TO_LABEL[slug]) return CITY_SLUG_TO_LABEL[slug];
  const normalizedSlug = slug.normalize("NFD").replace(/\p{M}/gu, "");
  const match = VEHICLE_SEARCH_CITY_OPTIONS.find(
    (c) =>
      c.toLowerCase() === slug ||
      c.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "") === normalizedSlug
  );
  return match || String(value).trim();
}

export function normalizeBodyTypeFilter(value) {
  if (!value || value === "Todos" || value === "all") return null;
  return BODY_TYPE_UI_TO_CANONICAL[value] || String(value).toLowerCase();
}

export function normalizeTransmissionFilter(value) {
  if (!value || value === "Todas" || value === "all") return null;
  const key = String(value).trim().toLowerCase();
  return TRANSMISSION_UI_TO_CANONICAL[key] || TRANSMISSION_UI_TO_CANONICAL[value] || key;
}

export function parseCsvParam(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

export function clampPage(value, fallback = 1) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(999, Math.floor(n));
}

export function clampPageSize(value, fallback = 24) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(60, Math.floor(n));
}
